// Block Clash / Tetris Battle Client (2-4 Players)
import { sounds } from '../sound.js';

export class TetrisGameClient {
  constructor(socket, containerEl) {
    this.socket = socket;
    this.container = containerEl;
    this.board = Array(20).fill(null).map(() => Array(10).fill(0));
    this.otherBoards = {};
    this.currentPiece = null;
    this.dropInterval = null;
    this.isDead = false;

    this.shapes = [
      [[1, 1, 1, 1]], // I
      [[1, 1], [1, 1]], // O
      [[0, 1, 0], [1, 1, 1]], // T
      [[1, 0, 0], [1, 1, 1]], // L
      [[0, 0, 1], [1, 1, 1]], // J
      [[0, 1, 1], [1, 1, 0]], // S
      [[1, 1, 0], [0, 1, 1]]  // Z
    ];
    this.colors = ['#38bdf8', '#fbbf24', '#c084fc', '#f97316', '#3b82f6', '#4ade80', '#ef4444'];

    this.render();
    this.setupSocketEvents();
    this.setupControls();
  }

  render() {
    this.container.innerHTML = `
      <div class="tetris-wrapper">
        <div class="tetris-header">
          <h2>🧱 BLOCK CLASH (TETRIS BATTLE)</h2>
          <div class="tetris-sub">Arrows/WASD: Move & Rotate • Space: Hard Drop • Clear 2+ lines to attack opponents!</div>
        </div>

        <div class="tetris-boards-area">
          <!-- Main Player Board -->
          <div class="tetris-main-box">
            <canvas id="tetris-my-canvas" width="240" height="480"></canvas>
          </div>

          <!-- Opponents Mini Boards -->
          <div class="tetris-opponents-box" id="tetris-opponents-box"></div>
        </div>

        <!-- Virtual Touch D-Pad for Mobile Players -->
        <div class="mobile-touch-bar">
          <div class="touch-dpad-cluster">
            <button id="touch-tetris-left" class="btn-virtual-touch">◀</button>
            <button id="touch-tetris-right" class="btn-virtual-touch">▶</button>
            <button id="touch-tetris-down" class="btn-virtual-touch">▼</button>
          </div>
          <div class="touch-action-cluster">
            <button id="touch-tetris-rotate" class="btn-virtual-touch">🔄</button>
            <button id="touch-tetris-drop" class="btn-virtual-touch btn-virtual-action">⚡ DROP</button>
          </div>
        </div>
      </div>
    `;

    this.canvas = document.getElementById('tetris-my-canvas');
    this.ctx = this.canvas.getContext('2d');
  }

  spawnPiece() {
    const shapeIdx = Math.floor(Math.random() * this.shapes.length);
    this.currentPiece = {
      shape: this.shapes[shapeIdx],
      color: shapeIdx + 1,
      x: 3,
      y: 0
    };

    if (this.checkCollision(this.currentPiece.x, this.currentPiece.y, this.currentPiece.shape)) {
      this.isDead = true;
      if (this.dropInterval) clearInterval(this.dropInterval);
      this.socket.emit('tetris_dead');
      sounds.playExplosion();
    }
  }

  setupControls() {
    const moveLeft = () => {
      if (this.isDead || !this.currentPiece) return;
      if (!this.checkCollision(this.currentPiece.x - 1, this.currentPiece.y, this.currentPiece.shape)) {
        this.currentPiece.x--;
        sounds.playPop();
        this.draw();
      }
    };

    const moveRight = () => {
      if (this.isDead || !this.currentPiece) return;
      if (!this.checkCollision(this.currentPiece.x + 1, this.currentPiece.y, this.currentPiece.shape)) {
        this.currentPiece.x++;
        sounds.playPop();
        this.draw();
      }
    };

    const doRotate = () => {
      if (this.isDead || !this.currentPiece) return;
      const rotated = this.rotate(this.currentPiece.shape);
      if (!this.checkCollision(this.currentPiece.x, this.currentPiece.y, rotated)) {
        this.currentPiece.shape = rotated;
        sounds.playPop();
        this.draw();
      }
    };

    const doHardDrop = () => {
      if (this.isDead || !this.currentPiece) return;
      while (!this.checkCollision(this.currentPiece.x, this.currentPiece.y + 1, this.currentPiece.shape)) {
        this.currentPiece.y++;
      }
      this.lockPiece();
    };

    this.keyHandler = (e) => {
      if (this.isDead || !this.currentPiece) return;
      const key = e.key ? e.key.toLowerCase() : '';

      if (e.code === 'KeyA' || key === 'a' || e.code === 'ArrowLeft') moveLeft();
      else if (e.code === 'KeyD' || key === 'd' || e.code === 'ArrowRight') moveRight();
      else if (e.code === 'KeyS' || key === 's' || e.code === 'ArrowDown') this.drop();
      else if (e.code === 'KeyW' || key === 'w' || e.code === 'ArrowUp') doRotate();
      else if (e.code === 'Space') doHardDrop();
    };

    const attachAction = (id, fn) => {
      const el = document.getElementById(id);
      if (!el) return;
      el.addEventListener('touchstart', (e) => { e.preventDefault(); fn(); }, { passive: false });
      el.addEventListener('click', fn);
    };

    attachAction('touch-tetris-left', moveLeft);
    attachAction('touch-tetris-right', moveRight);
    attachAction('touch-tetris-down', () => this.drop());
    attachAction('touch-tetris-rotate', doRotate);
    attachAction('touch-tetris-drop', doHardDrop);

    window.addEventListener('keydown', this.keyHandler);
  }

  rotate(matrix) {
    return matrix[0].map((_, i) => matrix.map(row => row[i]).reverse());
  }

  checkCollision(x, y, shape) {
    for (let r = 0; r < shape.length; r++) {
      for (let c = 0; c < shape[r].length; c++) {
        if (shape[r][c]) {
          const newX = x + c;
          const newY = y + r;
          if (newX < 0 || newX >= 10 || newY >= 20) return true;
          if (newY >= 0 && this.board[newY][newX]) return true;
        }
      }
    }
    return false;
  }

  drop() {
    if (!this.checkCollision(this.currentPiece.x, this.currentPiece.y + 1, this.currentPiece.shape)) {
      this.currentPiece.y++;
    } else {
      this.lockPiece();
    }
  }

  lockPiece() {
    this.currentPiece.shape.forEach((row, r) => {
      row.forEach((val, c) => {
        if (val) {
          const bY = this.currentPiece.y + r;
          const bX = this.currentPiece.x + c;
          if (bY >= 0 && bY < 20 && bX >= 0 && bX < 10) {
            this.board[bY][bX] = this.currentPiece.color;
          }
        }
      });
    });

    // Clear Lines
    let cleared = 0;
    for (let r = 19; r >= 0; r--) {
      if (this.board[r].every(cell => cell > 0)) {
        this.board.splice(r, 1);
        this.board.unshift(Array(10).fill(0));
        cleared++;
        r++; // Check same row index again
      }
    }

    if (cleared > 0) {
      sounds.playSuccess();
    }

    this.socket.emit('tetris_updateBoard', {
      board: this.board,
      linesCleared: cleared
    });

    this.spawnPiece();
  }

  setupSocketEvents() {
    this.socket.on('tetris_started', () => {
      this.isDead = false;
      this.board = Array(20).fill(null).map(() => Array(10).fill(0));
      this.spawnPiece();

      if (this.dropInterval) clearInterval(this.dropInterval);
      this.dropInterval = setInterval(() => {
        if (!this.isDead && this.currentPiece) {
          this.drop();
          this.draw();
        }
      }, 700);
    });

    this.socket.on('tetris_garbageSent', (data) => {
      if (data.fromId !== this.socket.id && !this.isDead) {
        sounds.playExplosion();
        // Add garbage rows to bottom
        for (let i = 0; i < data.count; i++) {
          this.board.shift();
          const garbageRow = Array(10).fill(8); // Grey block
          garbageRow[Math.floor(Math.random() * 10)] = 0; // 1 gap
          this.board.push(garbageRow);
        }
      }
    });

    this.socket.on('tetris_syncBoards', (data) => {
      this.otherBoards = data.boards;
      this.renderOpponents();
    });

    this.socket.on('tetris_gameOver', (data) => {
      sounds.playVictory();
      if (this.dropInterval) clearInterval(this.dropInterval);
      alert(`🏆 TETRIS BATTLE WINNER: ${data.winner?.name || 'Player'}!`);
    });
  }

  renderOpponents() {
    const oppBox = document.getElementById('tetris-opponents-box');
    if (!oppBox) return;
    oppBox.innerHTML = '';

    for (const id in this.otherBoards) {
      if (id === this.socket.id) continue;
      const wrap = document.createElement('div');
      wrap.className = 'mini-tetris-wrap';
      wrap.innerHTML = `<div class="opp-title">Opponent</div><canvas width="120" height="240"></canvas>`;
      const cvs = wrap.querySelector('canvas');
      const ctx = cvs.getContext('2d');

      const b = this.otherBoards[id];
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, 120, 240);

      b.forEach((row, r) => {
        row.forEach((val, c) => {
          if (val) {
            ctx.fillStyle = val === 8 ? '#64748b' : this.colors[val - 1] || '#38bdf8';
            ctx.fillRect(c * 12, r * 12, 11, 11);
          }
        });
      });
      oppBox.appendChild(wrap);
    }
  }

  draw() {
    if (!this.ctx) return;
    this.ctx.fillStyle = '#0f172a';
    this.ctx.fillRect(0, 0, 240, 480);

    // Board Grid
    this.board.forEach((row, r) => {
      row.forEach((val, c) => {
        if (val) {
          this.ctx.fillStyle = val === 8 ? '#64748b' : this.colors[val - 1];
          this.ctx.fillRect(c * 24, r * 24, 23, 23);
        }
      });
    });

    // Current Piece
    if (this.currentPiece) {
      this.ctx.fillStyle = this.colors[this.currentPiece.color - 1];
      this.currentPiece.shape.forEach((row, r) => {
        row.forEach((val, c) => {
          if (val) {
            this.ctx.fillRect((this.currentPiece.x + c) * 24, (this.currentPiece.y + r) * 24, 23, 23);
          }
        });
      });
    }
  }

  destroy() {
    if (this.dropInterval) clearInterval(this.dropInterval);
    if (this.keyHandler) window.removeEventListener('keydown', this.keyHandler);
    this.socket.off('tetris_started');
    this.socket.off('tetris_garbageSent');
    this.socket.off('tetris_syncBoards');
    this.socket.off('tetris_gameOver');
  }
}
